
import type { PropType } from 'vue'
import { computed, defineComponent, reactive, watch } from 'vue'

// components
import TheButton from '@/common/components/UI/TheButton/index.vue'
import TheSelect from '@/common/components/UI/TheSelect/index.vue'

// new-services
import timeSlotsAPI from '@/new-services/time-slots.service'

// types
import type { SenderT } from '@/core/types/senders.types'
import type { TimeSlotForCreateOrder } from '@/new-services/types/time-slots'

export default defineComponent({
    components: {
        TheButton,
        TheSelect
    },
    props: {
        title: {
            type: String,
            default: 'Выберите тайм-слот'
        },
        selectedText: {
            type: String,
            default: 'Выбранный тайм-слот: '
        },
        opened: {
            type: Boolean,
            default: false,
        },
        completed: {
            type: Boolean,
            default: false,
        },
        companyId: {
            type: Number as PropType<number | null>,
            default: null
        },
        sender: {
            type: Object as PropType<SenderT | null>,
            default: null
        },
        date: {
            type: String as PropType<string | null>,
            default: null
        },
        modelValue: {
            type: Object as PropType<TimeSlotForCreateOrder | null>,
            default: null
        },
        disabled: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['update:modelValue', 'submit', 'update'],
    setup(props, { emit }) {
        const state = reactive({
            is_loading: false,
            is_failed: false,
            list: [] as TimeSlotForCreateOrder[]
        })

        const formatFromTo = (from: string, to: string): { from: string; to: string } => {
            const formattedFrom = from.slice(0, 5)
            const [h, m, s] = to.split(':').map(Number)

            let endHour = h
            let endMinute = m

            if (m !== 0 || s !== 0) {
                endHour = (h + 1) % 24
                endMinute = 0
            }

            const formattedTo = `${String(endHour).padStart(2, '0')}:${String(endMinute).padStart(2, '0')}`

            return {
                from: formattedFrom,
                to: formattedTo,
            }
        }

        const loadTimeSlots = async () => {
            if (!props.sender || !props.date) {
                return
            }

            const sender = props.sender as SenderT & { dispatcher_sector_id: number; }

            try {
                state.is_loading = true

                const response = await timeSlotsAPI.getTimeSlotsForCreateOrder({
                    companyId: props.companyId,
                    dispatcherSectorId: sender.dispatcher_sector_id,
                    latitude: sender.latitude,
                    longitude: sender.longitude,
                    date: props.date,
                    typeId: 1,
                })

                state.list = response.data.data.map((ts) => {
                    const { from, to } = formatFromTo(ts.timeFrom, ts.timeTo)

                    return {
                        id: ts.id,
                        typeId: ts.typeId,
                        status: ts.status,
                        timeFrom: from,
                        timeTo: to
                    }
                })

                state.is_failed = false
            } catch (error) {
                console.error(error)
                state.is_failed = true
            } finally {
                state.is_loading = false
            }
        }

        const onTimeSlotChange = (timeSlotId: number) => {
            if (props.disabled) return

            const timeSlot = state.list.find((t) => t.id === timeSlotId)
            emit('update:modelValue', timeSlot)
        }

        const onSubmit = () => {
            if (props.disabled) return
            emit('submit')
        }

        const onUpdate = () => {
            if (props.disabled) return
            emit('update')
        }

        watch(
            () => props.date,
            (date) => {
                if (date) {
                    loadTimeSlots()
                }
            }
        )

        const selectedTimeSlotId = computed(() => {
            if (props.modelValue) {
                return props.modelValue.id
            }

            return null
        })

        return {
            state,
            onTimeSlotChange,
            loadTimeSlots,
            onSubmit,
            onUpdate,
            selectedTimeSlotId
        }
    }
})
